import type { ColorGradingConfig, ColumnColorGrading, ColorGradingThreshold } from "../datagrid-configs/types";

/**
 * Calculate relative luminance of a hex color and determine if text should be light or dark.
 * Uses WCAG formula: L = 0.2126 * R + 0.7152 * G + 0.0722 * B
 * @returns "white" for dark backgrounds, "black" for light backgrounds
 */
export function getContrastTextColor(hexColor: string): string {
	const hex = hexColor.replace("#", "");
	const r = parseInt(hex.substring(0, 2), 16) / 255;
	const g = parseInt(hex.substring(2, 4), 16) / 255;
	const b = parseInt(hex.substring(4, 6), 16) / 255;
	const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
	return luminance < 0.5 ? "#ffffff" : "#000000";
}

/**
 * Get the background color for a percentage value based on color grading configuration.
 * @param value The decimal value (0.0 - 1.0)
 * @param config The color grading configuration for this column
 * @returns The background color, or undefined if no color should be applied
 */
export function getColorForValue(
	value: number | null | undefined,
	config: ColumnColorGrading | undefined
): string | undefined {
	if (value == null || !config?.enabled || !config.thresholds || config.thresholds.length === 0) {
		return undefined;
	}

	// Sort thresholds descending to find the highest threshold that's <= value
	const sortedThresholds = [...config.thresholds].sort((a, b) => b.threshold - a.threshold);
	
	for (const threshold of sortedThresholds) {
		if (value >= threshold.threshold) {
			return threshold.color;
		}
	}

	// If value is below all thresholds, return the lowest threshold's color
	const lowestThreshold = sortedThresholds[sortedThresholds.length - 1];
	return lowestThreshold?.color;
}

/**
 * Default thresholds for percentage columns.
 * These represent a traffic light system: red (low) -> yellow (medium) -> green (high)
 */
export const DEFAULT_COLOR_THRESHOLDS: ColorGradingThreshold[] = [
	{ threshold: 0, color: "#fee2e2" },      // Light red (0-49%)
	{ threshold: 0.5, color: "#fef9c3" },    // Light yellow (50-74%)
	{ threshold: 0.75, color: "#dcfce7" },   // Light green (75-100%)
];

/**
 * List of percentage columns for CountriesTable
 */
export const COUNTRIES_PERCENTAGE_COLUMNS = [
	{ key: "consistentRunningPlayerPercent", label: "Consistent Running Player %", group: "Partner Club Stats" },
	{ key: "variablePlayerQualityPercent", label: "Variable Player Quality %", group: "Partner Club Stats" },
	{ key: "percentOfGamesTagged", label: "% of Games Tagged", group: "Game Volumes" },
	{ key: "proportionOfGameViews", label: "Proportion of Game Views", group: "Market Value" },
];

/**
 * List of percentage columns for ClubsTable
 */
export const CLUBS_PERCENTAGE_COLUMNS = [
	{ key: "consistentRunningPlayerPercent", label: "Consistent Running Player %", group: "Partner Club Stats" },
	{ key: "variablePlayerQualityPercent", label: "Variable Player Quality %", group: "Partner Club Stats" },
	{ key: "percentOfGamesTagged", label: "% of Games Tagged", group: "Game Volumes" },
	{ key: "proportionOfGameViews", label: "Proportion of Game Views", group: "Market Value" },
];

